/**
 * runTriggerPipeline — shared execution core for the scripting system.
 *
 * Pure logic — no I/O, no imports. Works identically in Node (ScriptRunner)
 * and the browser (EBScriptsRegistry). Both sides build their before/after
 * handler arrays and delegate here.
 *
 * Handlers are sorted by priority (ascending) internally — callers pass unsorted.
 *
 * All handlers receive and mutate one shared event object per dispatch.
 * Custom business payload lives on event.detail, similar to CustomEvent.
 *
 * Execution order:
 *   1. All before-handlers (sorted by priority, ascending)
 *   2. coreFn (skipped if ctx.preventDefault() was called)
 *   3. All after-handlers (sorted by priority, ascending)
 *
 * Control methods on event:
 *   event.stopPropagation()  — stop remaining handlers in the current phase only
 *   event.preventDefault()   — skip coreFn; both phases still complete
 *                              throws if canPreventDefault is false
 *   event.error("message")   — hard stop; no further execution
 *   event.output            — shared accumulator threaded through every call
 *
 * @param {Array<{ fn: Function, priority: number }>} beforeHandlers
 * @param {Array<{ fn: Function, priority: number }>} afterHandlers
 * @param {Function} coreFn - async (ctx) => void
 * @param {{
 *   type?: string,
 *   target?: any,
 *   currentTarget?: any,
 *   detail?: any,
 *   output?: any,
 *   canPreventDefault?: boolean,
 *   cancelable?: boolean,
 *   timeStamp?: number
 * }} [eventInit={}]
 * @returns {Promise<any>}
 */
export class ScriptTriggerError extends Error {
    constructor(message, event) {
        super(message || 'Script trigger error');
        this.name = 'ScriptTriggerError';
        this.code = 'EB_SCRIPT_TRIGGER_ERROR';
        this.event = event;
    }
}

export async function runTriggerPipeline(beforeHandlers, afterHandlers, coreFn, eventInit = {}) {
    const befores = [...beforeHandlers].sort((a, b) => a.priority - b.priority);
    const afters  = [...afterHandlers].sort((a, b) => a.priority - b.priority);

    let _stopPropagation        = false;
    let _error                  = null;

    const detail = eventInit.detail;

    const event = {
        type: eventInit.type || null,
        target: eventInit.target ?? null,
        currentTarget: eventInit.currentTarget ?? eventInit.target ?? null,
        timeStamp: eventInit.timeStamp ?? Date.now(),
        detail,
        output: eventInit.output,
        canPreventDefault: eventInit.canPreventDefault !== false,
        cancelable: eventInit.cancelable !== false,
        defaultPrevented: false,
        propagationStopped: false,
        stopPropagation() { _stopPropagation = true; this.propagationStopped = true; },
        preventDefault() {
            if (!this.canPreventDefault) {
                throw new ScriptTriggerError('preventDefault() is not allowed for this trigger', this);
            }
            if (this.cancelable) this.defaultPrevented = true;
        },
        error(msg) { _error = msg; this.defaultPrevented = true; _stopPropagation = true; this.propagationStopped = true; }
    };

    function captureOutput(returnValue) {
        if (returnValue !== undefined) event.output = returnValue;
    }

    const listenerThis = event.currentTarget ?? event.target ?? null;

    // --- Before phase ---
    for (const { fn } of befores) {
        captureOutput(await fn.call(listenerThis, event));
        if (_error) throw new ScriptTriggerError(_error, event);
        if (_stopPropagation) break;
    }

    // --- Core function ---
    if (!event.defaultPrevented) captureOutput(await coreFn.call(listenerThis, event));

    // Reset for after phase — stopPropagation is phase-scoped
    _stopPropagation = false;
    event.propagationStopped = false;

    // --- After phase ---
    for (const { fn } of afters) {
        captureOutput(await fn.call(listenerThis, event));
        if (_error) throw new ScriptTriggerError(_error, event);
        if (_stopPropagation) break;
    }

    return event.output;
}
